// src/components/flyer/FlyerLayoutModal.jsx
import { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";

import useSectorIntelligence from "../../hooks/useSectorIntelligence";
import AdvertSlotCard from "../AdvertSlotCard";

const TOTAL_SLOTS = 32;

export default function FlyerLayoutModal({
  sector: rawSector,
  slots = [],
  onBooked,
  isPublic = true,
}) {
  const isDev = import.meta.env.DEV;
  const [side, setSide] = useState("front");

  const sector =
    rawSector ||
    slots[0]?.postcode_sector ||
    slots[0]?.postcode ||
    "UNKNOWN";

  /* -------------------------------------------------------
     NORMALIZE INTO 32 FIXED SLOTS
  -------------------------------------------------------- */
  const normalized = useMemo(() => {
    const byNumber = new Map();
    (slots || []).forEach((s) => {
      if (s?.slot_number) byNumber.set(s.slot_number, s);
    });

    const result = [];
    for (let i = 1; i <= TOTAL_SLOTS; i++) {
      const existing = byNumber.get(i);

      result.push(
        existing || {
          id: `virtual-${i}`,
          slot_number: i,
          status: "available",
          offer_title: "",
          business_name: "",
          postcode: sector,
        }
      );
    }

    return result;
  }, [slots, sector]);

  /* -------------------------------------------------------
     ENRICH WITH SECTOR INTELLIGENCE
  -------------------------------------------------------- */
  const { enrichedSlots, error: intelligenceError } =
    useSectorIntelligence(sector, normalized);

  const workingSlots =
    enrichedSlots && enrichedSlots.length ? enrichedSlots : normalized;

  const left = useMemo(() => workingSlots.slice(0, 16), [workingSlots]);
  const right = useMemo(() => workingSlots.slice(16), [workingSlots]);

  const remainingCount = useMemo(
    () =>
      workingSlots.filter(
        (s) => s.status === "available" || s.status === "empty"
      ).length,
    [workingSlots]
  );

  useEffect(() => {
    if (isDev) {
      console.log(
        "[FlyerLayoutModal] sector:",
        sector,
        "slots:",
        workingSlots.length
      );
    }
  }, [sector, workingSlots, isDev]);

  useEffect(() => {
    setSide("front");
  }, [sector]);

  const getRemainingColor = () => {
    if (remainingCount > 20) return "green.500";
    if (remainingCount > 10) return "yellow.500";
    return "red.500";
  };

  /* -------------------------------------------------------
     SLOT WRAPPER — unified 170px height
  -------------------------------------------------------- */
  const SlotCard = ({ slot }) => {
    const isBranding = slot.status === "branding";
    const isAvailable =
      !isBranding &&
      (slot.status === "available" || slot.status === "empty");

    const handleBookClick = () => {
      if (!isPublic && isAvailable && onBooked) onBooked(slot);
    };

    return (
      <Tooltip
        label={
          isBranding
            ? "Branding slot"
            : isAvailable
            ? isPublic
              ? "Coupon Ad Slot"
              : "Click to book this slot"
            : slot.status === "booked"
            ? "Already booked"
            : "Not available"
        }
        hasArrow
        placement="top"
      >
        <Box
          position="relative"
          borderWidth="1px"
          borderColor="red.200"
          bg="white"
          rounded="md"
          p={2}
          height="170px"   // unified height
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <AdvertSlotCard
            slot={slot}
            variant={isBranding ? "branding" : isPublic ? "public" : "cockpit"}
          />

          {!isPublic && isAvailable && (
            <Button
              size="xs"
              colorScheme="red"
              mt={1}
              width="100%"
              onClick={handleBookClick}
            >
              Book
            </Button>
          )}

          {!isPublic && !isAvailable && !isBranding && (
            <Button size="xs" mt={1} width="100%" isDisabled variant="outline">
              {slot.status === "booked" ? "Booked" : "Unavailable"}
            </Button>
          )}

          {isBranding && (
            <Button size="xs" mt={1} width="100%" isDisabled variant="outline">
              Branding
            </Button>
          )}
        </Box>
      </Tooltip>
    );
  };

  /* -------------------------------------------------------
     GRID — unified gap = 1px
  -------------------------------------------------------- */
  const renderGrid = (chunk, label) => (
    <Grid
      templateColumns={{
        base: "repeat(1, 1fr)",
        sm: "repeat(2, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(4, 1fr)",
      }}
      gap={1}   // unified gap
    >
      {chunk.map((slot, idx) => (
        <GridItem key={slot.id || `${label}-${slot.slot_number || idx}`}>
          <SlotCard slot={slot} />
        </GridItem>
      ))}
    </Grid>
  );

  if (!sector) return null;

  /* -------------------------------------------------------
     MAIN RENDER — MOBILE SAFE FLIP
  -------------------------------------------------------- */
  return (
    <Box width="100%">
      <Flex justify="space-between" mb={3}>
        <Flex gap={2}>
          <Button
            size="xs"
            variant="outline"
            colorScheme="red"
            onClick={() => setSide(side === "front" ? "back" : "front")}
          >
            🔄 Flip to {side === "front" ? "Back" : "Front"}
          </Button>
        </Flex>

        {!isPublic && (
          <Text fontSize="xs" color={getRemainingColor()}>
            {remainingCount} left
          </Text>
        )}
      </Flex>

      {intelligenceError && !isPublic && (
        <Text fontSize="xs" color="red.500" mb={2}>
          Sector intelligence unavailable.
        </Text>
      )}

      <Box
        perspective="1600px"
        overflow="hidden"
        width="100%"
        minH="960px"
        position="relative"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={side}
            initial={{ rotateY: side === "front" ? -180 : 180, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: side === "front" ? 180 : -180, opacity: 0 }}
            transition={{ duration: 0.55, ease: "easeInOut" }}
            style={{
              transformStyle: "preserve-3d",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              willChange: "transform",
              position: "absolute",
              width: "100%",
              top: 0,
              left: 0,
            }}
          >
            {side === "front"
              ? renderGrid(left, "front")
              : renderGrid(right, "back")}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}
